import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import { filter, map } from 'rxjs';

import { Tour, tourMoveBlocker, toursOf } from '@core/utils/plan-edit/tours';
import { isNil } from '@core/utils/shared-utils';

import { TimelineRow } from '../models/timeline';
import { tourBlockHint } from './timeline-edit-menus';
import { TimelineEditService } from './timeline-edit.service';
import { TimelineGeometry, xOfDate } from './timeline-geometry';

interface Band {
  tour: Tour;
  left: number;
  width: number;
  start: string;
  end: string;
  movable: boolean;
  hint: string | null;
  /** The route's only tour: moving it moves every stop, as "Move all stops" does. */
  wholeRoute: boolean;
}

/** Bands are inset so two tours that meet still read as two. */
const BAND_INSET_PX = 2;

/**
 * A band under each tour of a route, in the strip below the blocks: the strip above them carries
 * the site pills. Dragging a band moves its tour; a tour sharing a job with another cannot move and
 * says why on hover.
 */
@Component({
  selector: 'app-timeline-edit-tours',
  imports: [TranslatePipe],
  templateUrl: './timeline-edit-tours.component.html',
  styleUrl: './timeline-edit-tours.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimelineEditToursComponent {
  readonly row = input.required<TimelineRow>();
  readonly geometry = input.required<TimelineGeometry>();

  protected readonly edit = inject(TimelineEditService);
  private readonly translate = inject(TranslateService);
  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );
  private readonly translateFn = (key: string, params?: Record<string, unknown>) =>
    this.translate.instant(key, params);

  protected readonly bands = computed<Band[]>(() => {
    this.currentLang();
    const row = this.row();
    const geometry = this.geometry();
    const tours = row.route ? toursOf(row.route) : [];
    const wholeRoute = tours.length === 1;
    return tours.map((tour) => {
      const start = new Date(tour.start);
      const end = new Date(tour.end);
      const left = xOfDate(geometry, start);
      const blocker = tourMoveBlocker(row.route!, tour, tours);
      const movable = this.edit.canDragTour(row, tour, blocker !== null);
      return {
        tour,
        left: left + BAND_INSET_PX,
        width: Math.max(0, xOfDate(geometry, end) - left - 2 * BAND_INSET_PX),
        start: format(start, 'HH:mm'),
        end: format(end, 'HH:mm'),
        movable,
        hint:
          tourBlockHint(blocker, tour, this.translateFn) ??
          (movable
            ? this.translate.instant(
                wholeRoute
                  ? 'features.plan.edit.tour.drag_hint_whole_route'
                  : 'features.plan.edit.tour.drag_hint'
              )
            : null),
        wholeRoute
      };
    });
  });

  /** The first action of the tour being dragged, whose band shows where it was lifted from. */
  protected readonly lifted = computed(() => {
    const drag = this.edit.drag();
    return drag?.carrying === 'tour' ? drag.actionUid : null;
  });

  /** The tour under the pointer, whose stops are outlined. */
  protected readonly hovered = signal<number | null>(null);

  protected onPointerDown(band: Band, ev: PointerEvent): void {
    const name = band.wholeRoute
      ? this.translate.instant('features.plan.edit.tour.all_stops', {
          route: this.row().name
        })
      : this.translate.instant('features.plan.edit.tour.name', { index: band.tour.index });
    this.edit.onTourPointerDown(this.row(), band.tour, name, ev);
  }
}
